<script lang="ts">
	//Hooks
	import { beforeUpdate } from 'svelte';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_local_ip,
	} from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { format } from 'svelte-i18n';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	//Design system components
	import {
		DeviceButton,
		DeviceButtonV2,
		type DeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';

	//Components
	import PinUnlock from '../../../components/PinUnlock.svelte';

	import { hasControlRights } from 'src/helpers/user_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { setCuryAwayMode } from '../../../helpers/cury_mode_helpers';
	import { formatEmptyVialMessage } from '../../../helpers/cury_vial_helpers';

	export let status, device;
	export let deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = { size: 's' };
	export let useV2Components = false;

	const structure = get_gen_2_component_channel_structure(device);

	// Check if away mode is active
	$: awayModeActive = status?.[structure]?.away_mode || false;

	$: leftSlot = status?.[structure]?.slots?.left;
	$: rightSlot = status?.[structure]?.slots?.right;
	$: vialFaultLeft = status?.[structure]?.slots?.left?.vial?.vial_fault;
	$: vialFaultRight = status?.[structure]?.slots?.right?.vial?.vial_fault;
	$: boostLeft = typeof leftSlot?.boost !== 'undefined' && leftSlot?.boost !== null;
	$: boostRight = typeof rightSlot?.boost !== 'undefined' && rightSlot?.boost !== null;
	$: disableLeft = leftSlot === null || vialFaultLeft; //Disable button and slider if boost is enabled or vial fault
	$: disableRight = rightSlot === null || vialFaultRight; //Disable button and slider if boost is enabled or vial fault
	$: isActiveLeft = leftSlot != null && leftSlot?.on && !disableLeft;
	$: isActiveRight = rightSlot != null && rightSlot?.on && !disableRight;
	$: leftIcon = !disableLeft ? (isActiveLeft ? 'fa fa-stop' : 'fa fa-spray-can') : 'fa fa-x';
	$: rightIcon = !disableRight ? (isActiveRight ? 'fa fa-stop' : 'fa fa-spray-can') : 'fa fa-x';

	$: leftAriaLabel =
		vialFaultLeft === 'empty'
			? formatEmptyVialMessage(
					status?.[structure]?.slots?.left?.vial?.name,
					$format,
					'label_vial_empty_named',
					'{vialName} is empty. Please replace with a new vial.',
					'label_vial_empty',
					'No liquid in vial',
				)
			: $format('label_turn_device', { default: 'Turn device' });
	$: rightAriaLabel =
		vialFaultRight === 'empty'
			? formatEmptyVialMessage(
					status?.[structure]?.slots?.right?.vial?.name,
					$format,
					'label_vial_empty_named',
					'{vialName} is empty. Please replace with a new vial.',
					'label_vial_empty',
					'No liquid in vial',
				)
			: $format('label_turn_device', { default: 'Turn device' });

	let loadingVial = { left: false, right: false }; //this is for toggle control

	let localIp = false;
	let pinLocked = { toggle: { slot: false, locked: false }, ambient: false };
	let showModal = false; // pin unlock modal

	$: hasRights = hasControlRights(device);

	// Timer state management - optimized structure
	const timerState = {
		left: { hasTimer: false, time: undefined, percentage: undefined },
		right: { hasTimer: false, time: undefined, percentage: undefined },
	};

	const calculate_timer = (device, status) => {
		const slots = status?.[structure]?.slots;
		const now = Math.round(new Date().getTime() / 1000);
		const data = {};

		if (!slots) {
			return data;
		}

		Object.keys(slots).forEach((slot) => {
			//rendering timers//Will keep this in case we need to do something with boost timers
			// if (slots[slot]?.boost?.started_at) {
			// 	//boost timers
			// 	const beginning = +slots[slot].boost.started_at;
			// 	const duration = +slots[slot].boost.duration;

			// 	const time = Math.round(beginning + duration) - now;

			// 	const percentage_remained =
			// 		(Math.floor(beginning + duration - now) / duration) * 100;

			// 	const ison = true;
			// 	data[slot] = [now, time, percentage_remained, ison];
			// }
			if (slots[slot]?.timer?.timer_started_at) {
				//ambient timers
				const beginning = +slots[slot].timer.timer_started_at.toFixed(0);
				const duration = +slots[slot].timer.timer_duration;
				const time = Math.round(beginning + duration) - now;

				const percentage_remained = (Math.floor(beginning + duration - now) / duration) * 100;

				const ison = status[structure]?.slots[slot]?.intensity;
				data[slot] = [now, time, percentage_remained, ison];
			}
		});
		return data;
	};

	// Optimized timer processing function
	const processTimerForSlot = (slot, slotData) => {
		const timerData = slotData?.timer;
		const hasTimer = timerData !== undefined && timerData !== null;
		const timerStarted = timerData?.timer_started_at;

		// Update timer state
		timerState[slot].hasTimer = hasTimer;

		// Only process if timer is active
		if (timerStarted && hasTimer) {
			const timerResult = calculate_timer(device, status)[slot];
			if (timerResult) {
				const [_, time, percentage, _ison] = timerResult;

				if (time !== undefined && percentage !== undefined) {
					timerState[slot].time = time;
					timerState[slot].percentage = Number(percentage.toFixed(0));
					return;
				}
			}
		}

		// Reset timer data if not active
		timerState[slot].time = undefined;
		timerState[slot].percentage = undefined;
	};

	//ToDo need to cleanup a bit more
	beforeUpdate(() => {
		const slots = status?.[structure]?.slots;
		localIp = get_local_ip(device.id);

		if (!slots) {
			return;
		}

		// Handle pin lock state
		if (typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock) {
			pinLocked.ambient = true;
			pinLocked.toggle.locked = true;
		}

		// Process timers only if device is online
		if (status?.online) {
			Object.keys(slots).forEach((slot) => {
				processTimerForSlot(slot, slots[slot]);
			});
		}
	});

	const handlePin = () => {
		// if (!pinLocked.ambient) {
		// 	handleTurn();
		// }
		if (!pinLocked.toggle.locked) {
			toggleIntensity(pinLocked.toggle.slot);
		}
		const unlock = unlockPin(device, pinLocked);
		pinLocked.toggle.locked = unlock;
		pinLocked.ambient = unlock;
	};

	const handleClose = () => {
		pinLocked.toggle.locked = true;
		pinLocked.ambient = true;
	};

	const disableAwayMode = async () => {
		await setCuryAwayMode(device, false);
	};

	const toggleIntensity = async (slot) => {
		if (!pinLocked.toggle.locked) {
			loadingVial[slot] = true;
			let res;
			const vialIntensity = status[structure]?.slots[slot]?.on;
			//http://192.168.33.1/rpc/Cury.Set?id=0&slot=%22left%22&on=false
			try {
				res = await gen2Request(device, 'cury.Set', {
					id: device.channel,

					slot: slot,
					on: !vialIntensity, //this is the on / off logic for the vial's intensity control
				});
				if (res?.isok) {
				}
			} catch (err) {
				console.log(err);
				loadingVial[slot] = false;
			}

			loadingVial[slot] = false;
			return res;
		} else {
			pinLocked.toggle.locked = false;
			pinLocked.toggle.slot = slot;
			showModal = true;
		}
	};
</script>

<div class="d-flex flex-full-height flex-direction-row justify-content-center">
	{#if awayModeActive}
		<!-- Show away mode control when active -->
		{#if useV2Components}
			<DeviceButtonV2
				id="devicebutton-away-mode-list"
				icon="fas fa-suitcase"
				aria-label={$format('button_away_mode', { default: 'Away Mode' })}
				aria-pressed={true}
				variant="primary"
				isActive={true}
				disabled={!hasRights}
				on:click={(e) => {
					e.stopPropagation();
					disableAwayMode();
				}}
				{...deviceButtonV2Props}
			/>
		{:else}
			<DeviceButton
				id="devicebutton-away-mode-list"
				icon="fas fa-suitcase"
				aria-label={$format('button_away_mode', { default: 'Away Mode' })}
				aria-pressed={true}
				variant="primary"
				isActive={true}
				disabled={!hasRights}
				on:click={(e) => {
					e.stopPropagation();
					disableAwayMode();
				}}
			/>
		{/if}
	{:else}
		{#key timerState['left'].percentage || timerState['right'].percentage || timerState['left'].hasTimer || timerState['right'].hasTimer}
			{#if useV2Components}
				<DeviceButtonV2
					id="devicebutton-y3dowa8"
					class="mr-s"
					icon={leftIcon}
					aria-label={leftAriaLabel}
					aria-pressed={isActiveLeft}
					variant={disableLeft ? 'danger' : 'primary'}
					hasTimer={timerState['left'].hasTimer}
					timer={timerState['left']}
					disabled={disableLeft || boostLeft || !hasRights}
					isActive={isActiveLeft}
					isLoading={loadingVial['left']}
					on:click={(e) => {
						e.stopPropagation();
						toggleIntensity('left');
					}}
					{...deviceButtonV2Props}
				/>
				<DeviceButtonV2
					id="devicebutton-80n5d9n"
					icon={rightIcon}
					aria-label={rightAriaLabel}
					aria-pressed={isActiveRight}
					variant={disableRight ? 'danger' : 'primary'}
					hasTimer={timerState['right'].hasTimer}
					timer={timerState['right']}
					disabled={disableRight || boostRight || !hasRights}
					isActive={isActiveRight}
					isLoading={loadingVial['right']}
					on:click={(e) => {
						e.stopPropagation();
						toggleIntensity('right');
					}}
					{...deviceButtonV2Props}
				/>
			{:else}
				<DeviceButton
					id="devicebutton-y3dowa8"
					class="mr-s"
					icon={leftIcon}
					aria-label={leftAriaLabel}
					aria-pressed={isActiveLeft}
					variant={disableLeft ? 'danger' : 'primary'}
					hasTimer={timerState['left'].hasTimer}
					timer={timerState['left']}
					disabled={disableLeft || boostLeft || !hasRights}
					isActive={isActiveLeft}
					isLoading={loadingVial['left']}
					on:click={(e) => {
						e.stopPropagation();
						toggleIntensity('left');
					}}
				/>
				<DeviceButton
					id="devicebutton-80n5d9n"
					icon={rightIcon}
					aria-label={rightAriaLabel}
					aria-pressed={isActiveRight}
					variant={disableRight ? 'danger' : 'primary'}
					hasTimer={timerState['right'].hasTimer}
					timer={timerState['right']}
					disabled={disableRight || boostRight || !hasRights}
					isActive={isActiveRight}
					isLoading={loadingVial['right']}
					on:click={(e) => {
						e.stopPropagation();
						toggleIntensity('right');
					}}
				/>
			{/if}
		{/key}
	{/if}
</div>

{#if showModal}
	<PinUnlock
		bind:showModal
		on:unlock={handlePin}
		on:close={handleClose}
		on:cancel={() => (showModal = false)}
	/>
{/if}
